/** One attached file, shown the way the console can: images inline (the
 *  rendition the model sees), text-bearing kinds in the document viewer
 *  (XML/JSON pretty-printed), everything else as facts plus a download.
 *  Bytes come through the API as blobs — the CSP allows `blob:` images but
 *  not S3, and inline PDF frames are blocked, so a PDF shows its extracted
 *  text and downloads for the real layout. */
import DownloadIcon from '@mui/icons-material/Download';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import {
  fetchAttachmentBlob,
  fetchAttachmentText,
  inlineVariant,
  isAttachmentExpired,
  type DiagnoseAttachment,
} from '@/api/diagnoseAttachments';
import { DocumentViewer } from '@/components/DocumentViewer';
import { useSnackbar } from '@/components/Snackbar';
import { downloadBlob } from '@/utils/download';
import { formatBytes } from '@/utils/formatBytes';

import { KIND_LABEL, KindIcon, modelCanRead } from './kind';

const EXPIRED = 'This file has expired — attachments are kept for one year.';

/** `KIND · size · dimensions/pages/chars` */
export function attachmentFacts(a: DiagnoseAttachment): string {
  const parts = [KIND_LABEL[a.kind], formatBytes(a.size)];
  if (a.width && a.height) parts.push(`${a.width}×${a.height}`);
  if (a.pages) parts.push(`${a.pages} page${a.pages > 1 ? 's' : ''}`);
  if (a.text_chars != null && a.kind !== 'image') parts.push(`${a.text_chars.toLocaleString()} chars`);
  return parts.join(' · ');
}

function baseName(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.slice(0, dot) : filename;
}

function ImageBody({ taskId, a }: { taskId: string; a: DiagnoseAttachment }) {
  const q = useQuery({
    queryKey: ['ai-diag', 'attachment', a.id, 'preview'],
    queryFn: () => fetchAttachmentBlob(taskId, a.id, 'preview'),
    staleTime: Infinity,
  });
  const url = useMemo(() => (q.data ? URL.createObjectURL(q.data) : null), [q.data]);
  useEffect(() => () => void (url && URL.revokeObjectURL(url)), [url]);
  if (q.isLoading) return <CircularProgress size={20} />;
  if (q.isError) {
    return <Alert severity={isAttachmentExpired(q.error) ? 'info' : 'error'}>{isAttachmentExpired(q.error) ? EXPIRED : 'The image could not be loaded.'}</Alert>;
  }
  return (
    <Box
      component="img"
      src={url ?? undefined}
      alt={a.filename}
      sx={{ display: 'block', maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 1, border: 1, borderColor: 'divider' }}
    />
  );
}

function TextBody({ taskId, a }: { taskId: string; a: DiagnoseAttachment }) {
  const q = useQuery({
    queryKey: ['ai-diag', 'attachment', a.id, 'text'],
    queryFn: () => fetchAttachmentText(taskId, a.id),
    staleTime: Infinity,
  });
  if (q.isLoading) return <CircularProgress size={20} />;
  if (q.isError) {
    return <Alert severity={isAttachmentExpired(q.error) ? 'info' : 'error'}>{isAttachmentExpired(q.error) ? EXPIRED : 'The text could not be loaded.'}</Alert>;
  }
  const hint = /json/i.test(a.content_type) ? 'json' : /xml|html|svg/i.test(a.content_type) ? 'xml' : undefined;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 320, maxHeight: '65vh' }}>
      <DocumentViewer
        text={q.data?.text ?? ''}
        formatHint={hint}
        downloadName={baseName(a.filename)}
        copyMessage="Text copied."
        emptyMessage="No text could be extracted from this file."
        beforeViewer={
          <>
            {a.kind !== 'text' && (
              <Typography variant="caption" color="text.secondary">
                Text extracted from the {KIND_LABEL[a.kind].toLowerCase()} — download it for the original layout.
              </Typography>
            )}
            {q.data?.truncated && (
              <Alert severity="info" sx={{ py: 0 }}>
                Only the beginning is shown (the first 2 MB, or 50 pages of a PDF) — download the file for the rest.
              </Alert>
            )}
          </>
        }
      />
    </Box>
  );
}

export function AttachmentPreview({ taskId, attachment: a }: { taskId: string; attachment: DiagnoseAttachment }) {
  const snackbar = useSnackbar();
  const download = useMutation({
    mutationFn: () => fetchAttachmentBlob(taskId, a.id, 'original'),
    onSuccess: (blob) => downloadBlob(blob, a.filename),
    onError: (e) => snackbar.show(isAttachmentExpired(e) ? EXPIRED : 'The download failed.', isAttachmentExpired(e) ? 'info' : 'error'),
  });
  const variant = inlineVariant(a.kind);

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <KindIcon kind={a.kind} contentType={a.content_type} color="action" />
        <Typography variant="body2" color="text.secondary">
          {attachmentFacts(a)}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={download.isPending ? <CircularProgress size={14} /> : <DownloadIcon />}
          disabled={download.isPending}
          onClick={() => download.mutate()}
        >
          Download original
        </Button>
      </Stack>
      {a.note && <Alert severity="warning">{a.note}</Alert>}
      {!modelCanRead(a.kind) && (
        <Alert severity="info">The AI sees only this file&apos;s name, type and size — not its content.</Alert>
      )}
      {variant === 'preview' && <ImageBody taskId={taskId} a={a} />}
      {variant === 'text' && <TextBody taskId={taskId} a={a} />}
    </Stack>
  );
}
